import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { readApiETag } from '@/features/pipeline/types';
import type { CommitteeDecision, CommitteeListItem, CommitteeResponse, CommitteeType } from './types';

export const committeesKey = (dealId: string) => ['committees', dealId] as const;

export function useCommittees(dealId: string | null) {
  return useQuery({
    queryKey: committeesKey(dealId ?? ''),
    enabled: !!dealId,
    queryFn: () => apiFetch<CommitteeListItem[]>(`/api/committees?dealId=${dealId}`),
  });
}

async function etagOf(id: string): Promise<string> {
  // Achado F8: readApiETag tolera a variação de casing do acrônimo.
  const c = await apiFetch<CommitteeResponse>(`/api/committees/${id}`);
  return readApiETag(c);
}

export interface CreateCommitteeInput {
  dealId: string;
  type: CommitteeType;
  scheduledDateUtc: string;
  participantUserIds: string[];
}

export function useCommitteeMutations(dealId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = () => void qc.invalidateQueries({ queryKey: committeesKey(dealId) });

  const create = useMutation({
    mutationFn: (input: CreateCommitteeInput) =>
      apiFetch<CommitteeResponse>('/api/committees', {
        method: 'POST',
        body: { ...input, status: 'Scheduled' },
      }),
    onSuccess: () => { invalidate(); toast('Comitê agendado.', 'default'); },
    onError: () => toast('Não foi possível agendar o comitê.', 'danger'),
  });

  const hold = useMutation({
    mutationFn: async (v: { id: string; heldDateUtc: string; decision: CommitteeDecision; minutesComment?: string | null }) =>
      apiFetch<CommitteeResponse>(`/api/committees/${v.id}/hold`, {
        method: 'POST',
        headers: { 'If-Match': `"${await etagOf(v.id)}"` },
        body: { heldDateUtc: v.heldDateUtc, decision: v.decision, minutesComment: v.minutesComment ?? null },
      }),
    onSuccess: () => { invalidate(); toast('Comitê realizado.', 'default'); },
    onError: () => toast('Não foi possível registrar a realização.', 'danger'),
  });

  const cancel = useMutation({
    mutationFn: async (v: { id: string; minutesComment?: string | null }) =>
      apiFetch<CommitteeResponse>(`/api/committees/${v.id}/cancel`, {
        method: 'POST',
        headers: { 'If-Match': `"${await etagOf(v.id)}"` },
        body: { minutesComment: v.minutesComment ?? null },
      }),
    onSuccess: () => { invalidate(); toast('Comitê cancelado.', 'default'); },
    onError: () => toast('Não foi possível cancelar.', 'danger'),
  });

  const postpone = useMutation({
    mutationFn: async (v: { id: string; scheduledDateUtc: string }) =>
      apiFetch<CommitteeResponse>(`/api/committees/${v.id}/postpone`, {
        method: 'POST',
        headers: { 'If-Match': `"${await etagOf(v.id)}"` },
        body: { scheduledDateUtc: v.scheduledDateUtc },
      }),
    onSuccess: () => { invalidate(); toast('Comitê adiado.', 'default'); },
    onError: () => toast('Não foi possível adiar.', 'danger'),
  });

  return { create, hold, cancel, postpone };
}
